import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import EditorLayout from "@/components/editor/EditorLayout";
import { TEMPLATES, newBlock } from "@/blocks/registry";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, FileText, Home, Pencil, Globe, FileEdit, Archive } from "lucide-react";
import { toast } from "sonner";

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-");

function StatusBadge({ page }) {
  if (page.archived) return <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#EDEAE2] text-[#5C635C]"><Archive className="w-3 h-3" /> בארכיון</span>;
  if (page.status === "published" && page.has_unpublished_changes)
    return <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800"><FileEdit className="w-3 h-3" /> שינויים שלא פורסמו</span>;
  if (page.status === "published")
    return <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-green-100 text-green-800"><Globe className="w-3 h-3" /> פורסם</span>;
  return <span className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full bg-[#E5E2D9] text-[#5C635C]"><FileEdit className="w-3 h-3" /> טיוטה</span>;
}

export default function Dashboard() {
  const [pages, setPages] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", slug: "", template: "landing", is_home: false });
  const [slugTouched, setSlugTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/pages").then((r) => setPages(r.data)).catch(() => setPages([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title.trim()) { toast.error("צריך שם לעמוד"); return; }
    const slug = form.slug.trim() || slugify(form.title);
    const tpl = TEMPLATES.find((t) => t.id === form.template);
    const blocks = tpl.blocks.map((type) => newBlock(type));
    setSaving(true);
    try {
      const r = await api.post("/pages", { title: form.title, slug, blocks, is_home: form.is_home });
      toast.success("העמוד נוצר");
      navigate(`/editor/pages/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail === "Slug already exists" ? "הכתובת (slug) כבר קיימת" : (err.response?.data?.detail || "יצירה נכשלה"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditorLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold">העמודים שלי</h1>
          <p className="text-[#5C635C] mt-1">כאן את יוצרת ועורכת את עמודי האתר.</p>
        </div>
        <Button data-testid="new-page-btn" onClick={() => { setForm({ title: "", slug: "", template: "landing", is_home: false }); setSlugTouched(false); setOpen(true); }}
                className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-2">
          <Plus className="w-4 h-4" /> עמוד חדש
        </Button>
      </div>

      {pages === null ? (
        <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[#4A5D4E] border-t-transparent rounded-full animate-spin" /></div>
      ) : pages.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-[#E5E2D9]">
          <FileText className="w-10 h-10 mx-auto text-[#c8c4b8] mb-3" />
          <p className="text-[#5C635C] mb-5">עדיין אין עמודים. בואי ניצור את הראשון!</p>
          <Button data-testid="empty-new-page-btn" onClick={() => setOpen(true)} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full gap-2"><Plus className="w-4 h-4" /> עמוד חדש</Button>
        </div>
      ) : (
        <div className="space-y-3">
          {pages.map((p) => (
            <button key={p.id} onClick={() => navigate(`/editor/pages/${p.id}`)} data-testid={`page-row-${p.id}`}
                    className="w-full text-right bg-white border border-[#E5E2D9] rounded-xl p-5 flex items-center justify-between hover:border-[#4A5D4E] hover:shadow-sm transition-all group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[#F2F5F1] flex items-center justify-center">
                  {p.is_home ? <Home className="w-5 h-5 text-[#4A5D4E]" /> : <FileText className="w-5 h-5 text-[#4A5D4E]" />}
                </div>
                <div>
                  <p className="font-semibold flex items-center gap-2">{p.title}{p.is_home && <span className="text-xs text-[#4A5D4E]">(דף הבית)</span>}</p>
                  <p className="text-sm text-[#8a8f89]" dir="ltr">/{p.slug}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <StatusBadge page={p} />
                <Pencil className="w-4 h-4 text-[#c8c4b8] group-hover:text-[#4A5D4E] transition-colors" />
              </div>
            </button>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent dir="rtl" className="font-editor" data-testid="create-page-dialog">
          <DialogHeader><DialogTitle className="text-right">עמוד חדש</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold mb-1.5">שם העמוד *</label>
              <Input data-testid="create-title" value={form.title}
                     onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, slug: slugTouched ? f.slug : slugify(e.target.value) }))}
                     placeholder="לדוגמה: About" />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1.5">כתובת בקישור (slug)</label>
              <Input data-testid="create-slug" value={form.slug} dir="ltr"
                     onChange={(e) => { setSlugTouched(true); setForm((f) => ({ ...f, slug: slugify(e.target.value) })); }}
                     placeholder="about" />
              <p className="text-xs text-[#8a8f89] mt-1">כך תיראה הכתובת של העמוד באתר. אותיות אנגליות ומקפים בלבד.</p>
            </div>
            <div>
              <label className="block text-sm font-semibold mb-2">תבנית פתיחה</label>
              <div className="grid grid-cols-2 gap-2">
                {TEMPLATES.map((t) => (
                  <button key={t.id} type="button" onClick={() => setForm((f) => ({ ...f, template: t.id }))}
                          data-testid={`template-${t.id}`}
                          className={`text-right p-3 rounded-lg border transition-colors ${form.template === t.id ? "border-[#4A5D4E] bg-[#F2F5F1]" : "border-[#E5E2D9] hover:border-[#c8c4b8]"}`}>
                    <p className="font-semibold text-sm">{t.label}</p>
                    <p className="text-xs text-[#8a8f89] mt-0.5">{t.description}</p>
                  </button>
                ))}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="create-is-home" checked={form.is_home} onChange={(e) => setForm((f) => ({ ...f, is_home: e.target.checked }))} className="accent-[#4A5D4E] w-4 h-4" />
              להגדיר כדף הבית של האתר
            </label>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">ביטול</Button>
            <Button data-testid="create-submit" onClick={create} disabled={saving} className="bg-[#4A5D4E] hover:bg-[#3d4d41] rounded-full">{saving ? "יוצר..." : "יצירה ועריכה"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </EditorLayout>
  );
}
